import { type MutableRefObject } from 'react';
import { type EventCallback } from '@vimeo/player';

import { trackEvent } from 'dibs-tracking';

type TrackVideo<Args = ObjectType> = (args: { vimeoId: number } & Args) => void;

const videoStarted: TrackVideo = ({ vimeoId }) => {
    trackEvent({
        action: 'video started',
        category: 'video',
        label: vimeoId,
    });
};
const videoPlaying: TrackVideo<{ percentComplete: number }> = ({ vimeoId, percentComplete }) => {
    trackEvent({
        action: 'video played for percentage',
        category: 'video',
        label: vimeoId,
        value: percentComplete,
    });
};

const videoFinished: TrackVideo = ({ vimeoId }) => {
    trackEvent({
        action: 'video finished',
        category: 'video',
        label: vimeoId,
    });
};

const videoPaused: TrackVideo = ({ vimeoId }) => {
    trackEvent({
        action: 'video paused',
        category: 'video',
        label: vimeoId,
    });
};

const initialTrackingState: [number, boolean][] = [
    [0, false],
    [0.25, false],
    [0.5, false],
    [0.75, false],
    [0.9, false],
    [1, false],
];

const percentCompleted =
    (
        trackingStateRef: MutableRefObject<typeof initialTrackingState>,
        vimeoId: number
    ): EventCallback =>
    ({ percent }) => {
        trackingStateRef.current.forEach(([targetPercent, fired], index) => {
            if (!fired) {
                if (targetPercent <= percent) {
                    trackingStateRef.current[index][1] = true;
                    if (targetPercent === 0) {
                        videoStarted({ vimeoId });
                    } else {
                        videoPlaying({ vimeoId, percentComplete: targetPercent });
                    }
                    // if target is 100% and both 90% is true and percent
                    // is very close to 100% or 0%, count video as finished
                } else if (
                    targetPercent === 1 &&
                    trackingStateRef.current[4][1] &&
                    (percent > 0.99 || percent <= 0.05)
                ) {
                    trackingStateRef.current[index][1] = true;
                    videoFinished({ vimeoId });
                }
            }
        });
    };

export default {
    percentCompleted,
    videoPaused,
    initialTrackingState,
};
